import { useLingui } from '@lingui/react/macro'
import { Button } from 'designSystem/components/Button'
import { Text } from 'designSystem/components/Text'
import { GlassPill } from 'features/knowledgeDocumentViewer/components/viewer/shared/glassSurface'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import React, { useCallback } from 'react'
import styled from 'styled-components'

const GlassNavigator = styled(GlassPill)`
  position: absolute;
  bottom: 0.75rem;
  left: 50%;
  z-index: 2;
  transform: translateX(-50%);
`

interface PreviewPageNavigatorProps {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  disabled?: boolean
}

export const PreviewPageNavigator: React.FC<PreviewPageNavigatorProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  disabled = false,
}) => {
  const { t } = useLingui()
  const handlePreviousPage = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation()
      if (disabled) return
      if (currentPage > 1) onPageChange(currentPage - 1)
    },
    [currentPage, disabled, onPageChange]
  )

  const handleNextPage = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation()
      if (disabled) return
      if (currentPage < totalPages) onPageChange(currentPage + 1)
    },
    [currentPage, disabled, onPageChange, totalPages]
  )

  if (totalPages <= 1) return null

  return (
    <GlassNavigator role='group' aria-label={t`Page navigation`}>
      <Button
        type='button'
        variant='ghost'
        size='icon-sm'
        onClick={handlePreviousPage}
        disabled={disabled || currentPage <= 1}
        aria-label={t`Previous page`}>
        <ChevronLeft />
      </Button>
      <Text variant='caption' weight='medium' wrap='nowrap'>
        {currentPage} / {totalPages}
      </Text>
      <Button
        type='button'
        variant='ghost'
        size='icon-sm'
        onClick={handleNextPage}
        disabled={disabled || currentPage >= totalPages}
        aria-label={t`Next page`}>
        <ChevronRight />
      </Button>
    </GlassNavigator>
  )
}
